/**
 * Block 14 «Раскрой» — PR #1 EP-1 (CUT-1.2). Zustand-стор шасси.
 *
 * **In-memory** (решение супервайзера PR #1 §1, вариант A) — по паттерну
 * `features/calculator/anketa/state/useCalculatorStore.ts`. Durable-персистенция
 * (IndexedDB-кэш `pomoshnik_cutting_cache`, синхронизация с региональным
 * Postgres P-007) — PR #2. localStorage для проектных данных НЕ используется
 * (TRD §7.9.3 / CLAUDE.md).
 *
 * Покрывает текущий редактируемый документ + черновики (in-session). Остальные
 * сущности (остатки/словари/тарифы) — по мере эпиков; имена ключей §0.4
 * зарезервированы в `storageKeys.ts`.
 */

import { create } from 'zustand';
import type {
  CuttingDraft,
  CuttingDraftPromoteInput,
  CuttingProject,
  CuttingProjectListItem,
  OptimizationResult,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { makeDefaultDocument } from '../model/defaults';
import { genUuid } from '@/lib/uuid';
import { isCompletePart, makeEmptyPart, mergeIdenticalParts } from '../model/partsUtils';
import { runCutting as runCuttingEngine } from '../lib/runCutting';
import { makeEmptyOffcut } from '../model/offcutsUtils';
import { WarehouseStore } from '../lib/warehouseStore';
import { CuttingProjectStore, type CuttingProjectRecord } from '../lib/cuttingProjectStore';
import { collectNewOffcuts } from '../lib/resultOffcuts';
import type {
  CuttingDocument,
  CuttingEdges,
  CuttingSettings,
  Part,
  ProjectMeta,
  WarehouseOffcut,
} from '../model/types';
import {
  documentToProjectData,
  draftToDocument,
  projectStateToUpsert,
  projectStateToDocument,
  type CuttingProjectState,
} from './projectSync';

/** DEV-only лог сбоев cutting-загрузки (P-042; в prod tree-shake'ится). */
function logCuttingError(op: string, err: unknown): void {
  if (import.meta.env.DEV) console.error(`[cutting] ${op} failed`, err);
}

/** Состояние асинхронного запуска раскроя (5 состояний, P-021). */
export type CutStatus = 'idle' | 'loading' | 'success' | 'empty' | 'error';

export interface CuttingStore {
  /** Текущий редактируемый документ раскроя. */
  document: CuttingDocument;
  /** Черновики in-session, keyed by id (durable-сохранение — PR #2). */
  drafts: Record<string, CuttingDocument>;

  // ── Результат раскроя (PR #21 EP-5, CUT-5.1, ADR-0079) ─────────────────
  /**
   * Транзиентный результат последнего раскроя (НЕ персистится — пересчитывается
   * клиентски движком). `project.data.lastResult` — отдельная контрактная копия
   * для сохранения проекта; этот слот — рабочий результат текущей сессии.
   */
  lastResult: OptimizationResult | null;
  /** Состояние запуска движка (idle/loading/success/empty/error). */
  cutStatus: CutStatus;
  /**
   * Запустить раскрой клиентски (главный поток, ADR-0079). Нет полных деталей →
   * `empty`; иначе `loading` → `optimize()` → `success` (+ `lastResult`) / `error`.
   */
  runCutting: () => void;

  // ── Сохранение остатков из результата в склад (PR #24-B EP-5) ──────────
  /**
   * Идемпотентность «Сохранить остатки» (§3.4): true после успешного сохранения
   * новых остатков текущего `lastResult`. Сбрасывается при новом `runCutting`
   * (старт loading) и смене проекта/черновика — иначе двойной клик дублирует.
   */
  resultOffcutsSaved: boolean;
  /**
   * Сохранить НОВЫЕ годные остатки последнего раскроя в склад (вкладка «Остатки»).
   * Берёт freeRects с ЦЕЛЫХ листов `lastResult` (§3.1), агрегирует одинаковые,
   * персистит через `WarehouseStore.add`. Возвращает кол-во добавленных записей
   * (0 — если нет результата / уже сохранено / нет годных остатков).
   */
  saveResultOffcutsToWarehouse: () => number;

  /** Полностью заменить документ. */
  setDocument: (document: CuttingDocument) => void;
  /** Частично обновить метаданные. */
  updateMeta: (patch: Partial<ProjectMeta>) => void;
  /** Частично обновить настройки движка. */
  updateSettings: (patch: Partial<CuttingSettings>) => void;
  /** Частично обновить кромки (вкладка «Настройки» → Кромки). */
  updateEdges: (patch: Partial<CuttingEdges>) => void;
  /** Заменить список деталей. */
  setParts: (parts: Part[]) => void;

  // ── Таблица деталей (PR #6a-PARTS, CUT-2.3/2.5) ────────────────────────
  /**
   * Добавить пустую деталь в конец. Возвращает созданную (для фокуса).
   * `seed` — переопределение полей пустой детали; используется фантомным рядом
   * PartsTab (материализация со стабильным заранее сгенерированным `id`, чтобы
   * React переиспользовал DOM-узел и не терял фокус). По умолчанию — `makeEmptyPart`.
   */
  addPart: (seed?: Partial<Part>) => Part;
  /** Частично обновить деталь по id. */
  updatePart: (id: string, patch: Partial<Part>) => void;
  /** Удалить деталь по id. */
  removePart: (id: string) => void;
  /** Дублировать деталь по id (копия с новым id — сразу после оригинала). */
  duplicatePart: (id: string) => void;
  /** Очистить все детали. */
  clearParts: () => void;
  /** Объединить одинаковые детали (L+W+name+edges → сумма qty, CUT-2.5). */
  mergeIdenticalParts: () => void;

  // ── Склад остатков + выбор «участвуют» (PR #24, ADR-0081) ──────────────
  /**
   * Reactive-зеркало склада остатков (cross-project, localStorage
   * `pomoshnik_offcuts_v1`). Таблица Остатков читает отсюда; правки персистятся в
   * `WarehouseStore`. НЕ part of `document` (склад общий между проектами).
   */
  warehouse: WarehouseOffcut[];
  /** Добавить пустую строку склада. Возвращает созданную (для фокуса). */
  addOffcut: () => WarehouseOffcut;
  /** Частично обновить запись склада по id (персист). */
  updateOffcut: (id: string, patch: Partial<WarehouseOffcut>) => void;
  /** Полное удаление записи склада по id (+ снять из выбора текущего проекта). */
  removeOffcut: (id: string) => void;
  /** Очистить весь склад (+ выбор). */
  clearOffcuts: () => void;
  /** Переключить участие складского остатка в раскрое текущего проекта (per-project). */
  toggleOffcutParticipation: (id: string) => void;
  /** Задать набор участвующих остатков (per-project). */
  setParticipatingOffcuts: (ids: string[]) => void;

  /** Сбросить документ к дефолтному (новый проект). */
  resetDocument: () => void;

  /** Снимок текущего документа в черновик по id. */
  saveDraft: (id: string) => void;
  /** Загрузить черновик в документ. `false` если черновика нет. */
  loadDraft: (id: string) => boolean;
  /** Удалить черновик. */
  deleteDraft: (id: string) => void;

  // ── Редактор + автосохранение (PR #4-SHELL, CUT-1.6) ───────────────────
  /** Есть несохранённые правки текущего документа (для автосейва). */
  dirty: boolean;
  /** id черновика для автосейва текущего документа (стабилен в рамках сессии). */
  currentDraftId: string | null;
  /** Сбросить dirty (после успешного автосейва). */
  markClean: () => void;
  /** Новый пустой проект в редакторе (свежий currentDraftId, dirty=false). */
  newProject: () => void;
  /** Загрузить черновик в редактор (document + currentDraftId, dirty=false). */
  adoptDraft: (draft: CuttingDraft) => void;
  /** Автосейв текущего документа как черновик, если dirty. `null` если чисто. */
  autosaveDraft: () => Promise<CuttingDraft | null>;

  // ── Серверная синхронизация (PR #3-DB EP-1, ADR-0065) ──────────────────
  /** Список проектов из стора (после `loadProjects`) — для экрана История. */
  projects: CuttingProjectListItem[];
  /** Драфты с сервера (после `loadDrafts`). */
  serverDrafts: CuttingDraft[];
  /**
   * id текущего сохранённого проекта (стабилен в рамках сессии редактирования) —
   * для идемпотентного «Сохранить проект» (PR #25 EP-5): повторный клик / сохранение
   * открытого проекта обновляет ТУ ЖЕ запись (upsert по client-UUID), не плодит дубли.
   * Устанавливается `loadProject` (= project.id) и первым `saveCurrentProject`. Сброс
   * (null) при новом/загруженном черновике/сбросе — это другой контекст редактирования.
   */
  currentProjectId: string | null;

  /** Загрузить список проектов из локального IDB в `projects` (История, ADR-0090). */
  loadProjects: () => Promise<void>;
  /** Серверный upsert-примитив (best-effort target; idempotent по client-UUID). */
  saveProject: (state: CuttingProjectState) => Promise<CuttingProject>;
  /**
   * «Сохранить проект» на экране «Результат» (PR #25 EP-5, local-first ADR-0090):
   * персист ТЕКУЩЕГО документа ВКЛЮЧАЯ результат раскроя (`lastResult` →
   * `data.lastResult`, контракт ADR-0065) в локальный IDB (`CuttingProjectStore`) —
   * durable источник истины устройства, работает офлайн/без бэкенда. Идемпотентно по
   * `currentProjectId` (reuse/generate). Имя — `meta.name` (fallback «Проект»);
   * `materialId` — `null` (B-254). Серверный sync — аддитивный best-effort (попытка
   * `saveProject` в try/catch; сбой НЕ ломает локальное сохранение, без ошибки юзеру).
   * Пустой `materialText` — локально OK, серверный best-effort пропускается (D2 min(1)).
   * Бросает ТОЛЬКО при сбое локального IDB (нет хранилища) — UI ловит в error-статус.
   */
  saveCurrentProject: () => Promise<void>;
  /** Загрузить проект по id из локального IDB в редактор (`document` + регидрация результата). */
  loadProject: (id: string) => Promise<void>;
  /** Удалить проект из локального IDB + `projects` (+ best-effort сервер). */
  deleteProject: (id: string) => Promise<void>;
  /** Загрузить драфты с сервера в `serverDrafts`. */
  loadDrafts: () => Promise<void>;
  /** Сохранить текущий `document` как драфт на сервере (по client-UUID). */
  saveDraftToServer: (id: string, name: string) => Promise<CuttingDraft>;
  /** Promote драфта в проект (создаёт project из draft.data, удаляет драфт). */
  promoteDraft: (id: string, input: CuttingDraftPromoteInput) => Promise<CuttingProject>;

  // ── Тогглы карты раскроя (UI-only, PR map-toggle-print) ────────────────
  /**
   * Показывать кромки на карте раскроя (тоггл «👁 Кромки»). default `true`.
   * Транзиентное UI-состояние — экран, печать и PDF читают это одно значение,
   * поэтому тоггл управляет и печатью, и PDF. НЕ персистится (в `document`/
   * serialization/roundtrip НЕ входит) — это не часть проекта.
   */
  mapShowEdges: boolean;
  /** Показывать пазы на карте раскроя (тоггл «Пазы»). default `true`. UI-only (см. `mapShowEdges`). */
  mapShowGrooves: boolean;
  /** Задать видимость кромок на карте (экран/печать/PDF). */
  setMapShowEdges: (v: boolean) => void;
  /** Задать видимость пазов на карте (экран/печать/PDF). */
  setMapShowGrooves: (v: boolean) => void;

  /** Полная очистка (logout). */
  clearAll: () => void;
}

export const useCuttingStore = create<CuttingStore>((set, get) => ({
  document: makeDefaultDocument(),
  drafts: {},
  warehouse: WarehouseStore.getAll(), // гидрация из localStorage при инициализации

  // ── Результат раскроя (PR #21 EP-5) ────────────────────────────────────
  lastResult: null,
  cutStatus: 'idle',
  resultOffcutsSaved: false,

  // ── Тогглы карты раскроя (UI-only) ─────────────────────────────────────
  mapShowEdges: true,
  mapShowGrooves: true,
  setMapShowEdges: (v) => set({ mapShowEdges: v }),
  setMapShowGrooves: (v) => set({ mapShowGrooves: v }),

  runCutting: () => {
    const { document } = get();
    if (!document.parts.some(isCompletePart)) {
      set({ cutStatus: 'empty', lastResult: null, resultOffcutsSaved: false });
      return;
    }
    // Сброс идемпотентности до счёта: новый раскрой → новые остатки можно сохранить.
    set({ cutStatus: 'loading', resultOffcutsSaved: false });
    // Откладываем синхронный optimize() на макрозадачу — спиннер 'loading'
    // успевает отрисоваться до блокирующего счёта (ADR-0079, главный поток).
    setTimeout(() => {
      try {
        const result = runCuttingEngine(get().document, get().warehouse);
        set({ lastResult: result, cutStatus: 'success' });
      } catch (err) {
        logCuttingError('runCutting', err);
        set({ lastResult: null, cutStatus: 'error' });
      }
    }, 0);
  },

  saveResultOffcutsToWarehouse: () => {
    const { lastResult, resultOffcutsSaved, document } = get();
    if (!lastResult || resultOffcutsSaved) return 0;
    const items = collectNewOffcuts(lastResult, {
      threshold: document.settings.minOffcutSize,
      namePrefix: `От ${document.meta.name.trim() || 'проект'}`,
      projectMaterial: document.meta.materialText,
      // includeOffcutSheets: false (§3.1 — только целые листы), aggregate: true (§3.2)
    });
    if (items.length === 0) return 0;
    // add() сам проставит id (genUuid) и нормализует; пустых здесь нет (freeRects > 0).
    const warehouse = WarehouseStore.add(items.map((o) => ({ ...o, id: '' })));
    set({ warehouse, resultOffcutsSaved: true });
    return items.length;
  },

  setDocument: (document) => set({ document, dirty: true }),

  updateMeta: (patch) =>
    set((s) => ({
      document: { ...s.document, meta: { ...s.document.meta, ...patch } },
      dirty: true,
    })),

  updateSettings: (patch) =>
    set((s) => ({
      document: { ...s.document, settings: { ...s.document.settings, ...patch } },
      dirty: true,
    })),

  updateEdges: (patch) =>
    set((s) => ({
      document: { ...s.document, edges: { ...s.document.edges, ...patch } },
      dirty: true,
    })),

  setParts: (parts) => set((s) => ({ document: { ...s.document, parts }, dirty: true })),

  // ── Таблица деталей ────────────────────────────────────────────────────
  addPart: (seed) => {
    const part = seed ? { ...makeEmptyPart(), ...seed } : makeEmptyPart();
    set((s) => ({ document: { ...s.document, parts: [...s.document.parts, part] }, dirty: true }));
    return part;
  },

  updatePart: (id, patch) =>
    set((s) => ({
      document: {
        ...s.document,
        parts: s.document.parts.map((p) => (p.id === id ? { ...p, ...patch } : p)),
      },
      dirty: true,
    })),

  removePart: (id) =>
    set((s) => ({
      document: { ...s.document, parts: s.document.parts.filter((p) => p.id !== id) },
      dirty: true,
    })),

  duplicatePart: (id) =>
    set((s) => {
      const idx = s.document.parts.findIndex((p) => p.id === id);
      const src = s.document.parts[idx];
      if (!src) return s;
      const clone: Part = {
        ...src,
        id: genUuid(),
        edges: { ...src.edges },
        grooves: { ...src.grooves }, // PR #10b-1: пазы deep-clone (не общая ссылка)
      };
      const parts = [...s.document.parts];
      parts.splice(idx + 1, 0, clone);
      return { document: { ...s.document, parts }, dirty: true };
    }),

  clearParts: () => set((s) => ({ document: { ...s.document, parts: [] }, dirty: true })),

  mergeIdenticalParts: () =>
    set((s) => ({
      document: { ...s.document, parts: mergeIdenticalParts(s.document.parts) },
      dirty: true,
    })),

  // ── Склад остатков + выбор «участвуют» (PR #24, ADR-0081) ──────────────
  // Склад (warehouse) — cross-project; правки персистятся в localStorage и НЕ
  // помечают документ dirty. Выбор (participatingOffcutIds) — per-project →
  // помечает документ dirty (durable через ProjectData, projectSync).

  addOffcut: () => {
    const offcut = makeEmptyOffcut();
    set((s) => {
      const warehouse = [...s.warehouse, offcut];
      WarehouseStore.save(warehouse);
      return { warehouse };
    });
    return offcut;
  },

  updateOffcut: (id, patch) =>
    set((s) => {
      const warehouse = s.warehouse.map((o) => (o.id === id ? { ...o, ...patch } : o));
      WarehouseStore.save(warehouse);
      return { warehouse };
    }),

  removeOffcut: (id) =>
    set((s) => {
      const warehouse = s.warehouse.filter((o) => o.id !== id);
      WarehouseStore.save(warehouse);
      // снять удалённый из выбора текущего проекта (per-project, dirty)
      const ids = s.document.participatingOffcutIds;
      const nextIds = ids.filter((x) => x !== id);
      if (nextIds.length === ids.length) return { warehouse };
      return {
        warehouse,
        document: { ...s.document, participatingOffcutIds: nextIds },
        dirty: true,
      };
    }),

  clearOffcuts: () =>
    set((s) => {
      WarehouseStore.save([]);
      const hadSelection = s.document.participatingOffcutIds.length > 0;
      return {
        warehouse: [],
        ...(hadSelection
          ? { document: { ...s.document, participatingOffcutIds: [] }, dirty: true }
          : {}),
      };
    }),

  toggleOffcutParticipation: (id) =>
    set((s) => {
      const ids = s.document.participatingOffcutIds;
      const nextIds = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
      return { document: { ...s.document, participatingOffcutIds: nextIds }, dirty: true };
    }),

  setParticipatingOffcuts: (ids) =>
    set((s) => ({
      document: { ...s.document, participatingOffcutIds: [...ids] },
      dirty: true,
    })),

  resetDocument: () =>
    set({
      document: makeDefaultDocument(),
      currentProjectId: null,
      lastResult: null,
      cutStatus: 'idle',
      resultOffcutsSaved: false,
    }),

  saveDraft: (id) => set((s) => ({ drafts: { ...s.drafts, [id]: s.document } })),

  loadDraft: (id) => {
    const draft = get().drafts[id];
    if (!draft) return false;
    set({
      document: draft,
      currentProjectId: null,
      lastResult: null,
      cutStatus: 'idle',
      resultOffcutsSaved: false,
    });
    return true;
  },

  deleteDraft: (id) =>
    set((s) => {
      if (!(id in s.drafts)) return s;
      const next = { ...s.drafts };
      delete next[id];
      return { drafts: next };
    }),

  // ── Редактор + автосохранение ─────────────────────────────────────────
  dirty: false,
  currentDraftId: null,

  markClean: () => set({ dirty: false }),

  newProject: () =>
    set({
      document: makeDefaultDocument(),
      currentProjectId: null,
      currentDraftId: genUuid(),
      dirty: false,
      lastResult: null,
      cutStatus: 'idle',
      resultOffcutsSaved: false,
    }),

  adoptDraft: (draft) =>
    set({
      document: draftToDocument(draft),
      currentProjectId: null,
      currentDraftId: draft.id,
      dirty: false,
      lastResult: null,
      cutStatus: 'idle',
      resultOffcutsSaved: false,
    }),

  autosaveDraft: async () => {
    const { dirty, currentDraftId, document } = get();
    if (!dirty) return null;
    const id = currentDraftId ?? genUuid();
    if (currentDraftId === null) set({ currentDraftId: id });
    const name = document.meta.name.trim() || 'Черновик';
    try {
      const draft = await get().saveDraftToServer(id, name);
      get().markClean();
      return draft;
    } catch (err) {
      // Без backend/auth (preview/prod до Phase 9) автосейв не должен кидать
      // uncaught-in-promise; dirty НЕ сбрасываем — повторим на следующем тике.
      logCuttingError('autosaveDraft', err);
      return null;
    }
  },

  // ── Серверная синхронизация (api-client под MSW в тестах) ──────────────
  projects: [],
  serverDrafts: [],
  currentProjectId: null,

  // Авто-загрузка на mount экранов (История): локальный IDB — источник истины
  // устройства (ADR-0090). При сбое (нет IDB — приватный режим) деградируем в пустое
  // состояние, НЕ кидаем (нет ErrorBoundary → белый экран).
  loadProjects: async () => {
    try {
      const records = await CuttingProjectStore.listProjects();
      set({ projects: records.map(projectRecordToListItem) });
    } catch (err) {
      logCuttingError('loadProjects', err);
    }
  },

  saveProject: async (state) => {
    const project = await apiClient.cutting.saveProject(projectStateToUpsert(state));
    return project;
  },

  saveCurrentProject: async () => {
    const { document, lastResult, currentProjectId } = get();
    const id = currentProjectId ?? genUuid();
    if (currentProjectId === null) set({ currentProjectId: id });
    // lastResult → data.lastResult (контракт ADR-0065): результат раскроя сохраняется
    // вместе с проектом и регидрируется при переоткрытии (`loadProject` → lastResult).
    const data = documentToProjectData(document, lastResult ? { lastResult } : {});
    const m = document.meta;
    const state: CuttingProjectState = {
      id,
      name: m.name.trim() || 'Проект',
      materialText: m.materialText, // local-first: пустой OK (серверный best-effort пропустится)
      materialId: null, // документ не несёт materialId (B-254, существующее ограничение)
      status: 'draft',
      ...(m.customerName !== undefined ? { customerName: m.customerName } : {}),
      ...(m.dueDate !== undefined ? { dueDate: m.dueDate } : {}),
      ...(m.notes !== undefined ? { notes: m.notes } : {}),
      data,
    };
    const record: CuttingProjectRecord = {
      ...state,
      updatedAt: new Date().toISOString(),
      syncStatus: 'local',
    };
    // 1) ЛОКАЛЬНО (durable, источник истины) — бросает только если IDB недоступен.
    await CuttingProjectStore.saveProject(record);
    // 2) Обновить список Истории из локальной записи (мгновенно, без перезагрузки).
    set((s) => ({
      projects: [projectRecordToListItem(record), ...s.projects.filter((p) => p.id !== record.id)],
    }));
    // 3) Серверный sync — АДДИТИВНЫЙ best-effort (ADR-0090): сбой НЕ ломает локальное
    //    сохранение и НЕ показывает ошибку. Пустой materialText → пропускаем (D2 min(1)).
    if (record.materialText.trim()) {
      void get()
        .saveProject(state)
        .then(() => {
          // Пометить synced (повторный put не критичен; молча игнорируем сбой записи).
          void CuttingProjectStore.saveProject({ ...record, syncStatus: 'synced' }).catch(() => {});
        })
        .catch((err) => logCuttingError('saveCurrentProject:sync', err));
    }
  },

  loadProject: async (id) => {
    // Локальный IDB — источник истины устройства (ADR-0090). История листает только
    // локальные записи, поэтому открываемый id всегда здесь. Нет записи → бросаем
    // (History.openProject ловит, не уходит на пустой экран).
    const record = await CuttingProjectStore.getProject(id);
    if (!record) throw new Error(`CUTTING_PROJECT_NOT_FOUND: id=${id}`);
    // Восстановить сохранённый результат раскроя (data.lastResult) в живой слот —
    // вкладка «Результат» + печать доступны СРАЗУ при переоткрытии (PR #25).
    const savedResult = record.data.lastResult ?? null;
    set({
      document: projectStateToDocument(record),
      currentProjectId: record.id, // открытый проект → «Сохранить проект» обновляет его
      currentDraftId: null,
      dirty: false,
      lastResult: savedResult,
      cutStatus: savedResult ? 'success' : 'idle',
      resultOffcutsSaved: false,
    });
  },

  deleteProject: async (id) => {
    // Локально (источник истины) + список Истории; серверный best-effort — аддитивно.
    await CuttingProjectStore.deleteProject(id);
    set((s) => ({ projects: s.projects.filter((p) => p.id !== id) }));
    void apiClient.cutting
      .deleteProject(id)
      .catch((err) => logCuttingError('deleteProject:sync', err));
  },

  loadDrafts: async () => {
    try {
      const res = await apiClient.cutting.listDrafts();
      set({ serverDrafts: res.items });
    } catch (err) {
      logCuttingError('loadDrafts', err);
    }
  },

  saveDraftToServer: async (id, name) => {
    const data = documentToProjectData(get().document);
    const draft = await apiClient.cutting.saveDraft({ id, name, data });
    set((s) => ({ serverDrafts: [draft, ...s.serverDrafts.filter((d) => d.id !== draft.id)] }));
    return draft;
  },

  promoteDraft: async (id, input) => {
    const project = await apiClient.cutting.promoteDraft(id, input);
    set((s) => ({ serverDrafts: s.serverDrafts.filter((d) => d.id !== id) }));
    return project;
  },

  clearAll: () => {
    // Logout: чистим локальные проекты устройства (best-effort, async — не блокируем set).
    void CuttingProjectStore.clear().catch(() => {});
    set({
      document: makeDefaultDocument(),
      drafts: {},
      projects: [],
      serverDrafts: [],
      currentProjectId: null,
      dirty: false,
      currentDraftId: null,
      lastResult: null,
      cutStatus: 'idle',
      resultOffcutsSaved: false,
    });
  },
}));

/** Локальная IDB-запись проекта (ADR-0090) → элемент списка `projects` (Истории). */
function projectRecordToListItem(record: CuttingProjectRecord): CuttingProjectListItem {
  return {
    id: record.id,
    name: record.name,
    materialText: record.materialText,
    status: record.status,
    customerName: record.customerName,
    lastKim: record.data.lastResult?.metrics.kim,
    sheetCount: record.data.lastResult?.sheets.length,
    updatedAt: record.updatedAt,
  };
}

/** Тестовый утилит — сброс стора между тестами. Не для production-кода. */
export function resetCuttingStoreForTests(): void {
  WarehouseStore.clear(); // изоляция: чистим localStorage-склад между тестами
  useCuttingStore.setState({
    document: makeDefaultDocument(),
    drafts: {},
    warehouse: [],
    projects: [],
    serverDrafts: [],
    currentProjectId: null,
    dirty: false,
    currentDraftId: null,
    lastResult: null,
    cutStatus: 'idle',
    resultOffcutsSaved: false,
    mapShowEdges: true,
    mapShowGrooves: true,
  });
}
